'use client'

import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'
import { api } from '@/convex/_generated/api'
import { useSoftAuthedQuery } from '@/hooks/use-authed-query'
import { isConvexOrgReady, useConvexOrgId } from '@/hooks/use-convex-org-id'
import {
  emptyOrgAccess,
  isOrgAdminAccess,
  type OrgAccessSnapshot,
  type OrgContextSeed,
} from '@/lib/auth/org-access'

/**
 * Org identity + the caller's access, for every client surface.
 *
 * `access` and `orgAccess` are SEEDED by the server shell from the same
 * Convex read that gates brand setup, so the first paint is already
 * role-aware (no extra round trip), then kept live by the fail-soft
 * `getMyOrgAccess` subscription so a role change lands without a reload.
 * The subscription is held until the Convex JWT org claim matches the
 * seeded org, exactly like the brand list in `contexts/brand-context.tsx`.
 */
export type OrgContextValue = OrgContextSeed

const OrgReactContext = createContext<OrgContextValue | null>(null)

export function OrgContextProvider({
  value,
  children,
}: {
  value: OrgContextSeed | null
  children: ReactNode
}) {
  if (!value) {
    return (
      <OrgReactContext.Provider value={null}>
        {children}
      </OrgReactContext.Provider>
    )
  }
  return (
    <LiveOrgContextProvider key={`${value.orgId}:${value.userId}`} seed={value}>
      {children}
    </LiveOrgContextProvider>
  )
}

function accessOf(snapshot: OrgAccessSnapshot): 'admin' | 'member' {
  return isOrgAdminAccess(snapshot) ? 'admin' : 'member'
}

function LiveOrgContextProvider({
  seed,
  children,
}: {
  seed: OrgContextSeed
  children: ReactNode
}) {
  const convexOrgId = useConvexOrgId()
  const isOrgReady = isConvexOrgReady(convexOrgId, seed.orgId)
  const live = useSoftAuthedQuery(
    api.memberRoles.queries.getMyOrgAccess,
    isOrgReady ? { orgId: seed.orgId } : 'skip'
  )
  const liveAccess: OrgAccessSnapshot | undefined = live.data
  // The most recent live answer, kept across a later outage: a demotion the
  // subscription delivered is never revived by a degraded read.
  const [lastLiveAccess, setLastLiveAccess] =
    useState<OrgAccessSnapshot | null>(null)
  if (liveAccess !== undefined && liveAccess !== lastLiveAccess) {
    setLastLiveAccess(liveAccess)
  }
  const hasWrongOrg = convexOrgId !== undefined && convexOrgId !== seed.orgId
  const value = useMemo<OrgContextValue>(() => {
    if (hasWrongOrg) {
      return { ...seed, access: 'member', orgAccess: emptyOrgAccess('member') }
    }
    if (liveAccess) {
      return {
        userId: seed.userId,
        orgId: seed.orgId,
        access: accessOf(liveAccess),
        orgAccess: liveAccess,
      }
    }
    if (!(lastLiveAccess || live.isError)) {
      return seed
    }
    // No live answer right now (loading again, or a degraded subscription):
    // the latest verified answer stands, fail-closed on grants the way the
    // shell treats a degraded read — the last live answer once one landed,
    // else the server-verified seed (an admin keeps their controls; the
    // server still admits them).
    const access = lastLiveAccess ? accessOf(lastLiveAccess) : seed.access
    return { ...seed, access, orgAccess: emptyOrgAccess(access) }
  }, [seed, liveAccess, lastLiveAccess, live.isError, hasWrongOrg])
  return (
    <OrgReactContext.Provider value={value}>
      {children}
    </OrgReactContext.Provider>
  )
}

export function useOptionalOrgContext(): OrgContextValue | null {
  return useContext(OrgReactContext)
}

export function useOrgContext(): OrgContextValue {
  const context = useOptionalOrgContext()

  if (!context) {
    throw new Error('useOrgContext must be used within an OrgContextProvider')
  }

  return context
}

/** The caller's org role and brand grants (seeded, then live). */
export function useOrgAccess(): OrgAccessSnapshot {
  return useOrgContext().orgAccess
}

/** The same snapshot, or null outside an `OrgContextProvider` (fixtures, previews). */
export function useOptionalOrgAccess(): OrgAccessSnapshot | null {
  return useOptionalOrgContext()?.orgAccess ?? null
}
